import React, { useMemo, useCallback, useState } from 'react';
import {
  Row, 
  Col,
  InputGroup,
  Input,
  Media, 
  Badge,
  Card,
  CardBody,
  CardFooter,
  Table,
  CustomInput,
  DropdownItem,
  DropdownToggle,
  DropdownMenu,
  UncontrolledDropdown,
  Breadcrumb,
  BreadcrumbItem,
  ListGroupItem,
  ListGroup,
  Spinner
} from 'reactstrap';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { fetchUserFormList, fetchUserFormAnalytics } from 'api/actions/forms'
import { fetcher } from 'api/client'
import { useQuery } from 'react-query'
import { useTable } from 'react-table'
import { useTranslation } from 'react-i18next'
import { RadialChart } from 'react-vis';

import ReactTable from 'components/Shared/ReactTable'
import { MainHeader } from 'components/Shared/MainHeader'
import Pagination from 'components/Shared/Paginator';
import ItemDetail from 'components/Shared/ItemDetail';
import { UserFormDetail } from './UserFormDetail/UserFormDetail';
import useDebounce from 'hooks/useDebounce';
import { parseDate, truncate, noop, getKey, getDateDiffInDays } from 'utils';
import { insertEmptyElement } from '@udecode/slate-plugins';
import { useProfileState } from 'context/profile';


function getBadgeColor(days) {
  if (days <= 2)
      return 'success';
  if (days >= 3 && days <= 4)
      return 'warning';
  else
      return 'danger';
}

export default function UserFormList() {
  const  { t } = useTranslation()
  const [currentUserForm, setCurrentUserForm] = useState({});
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [searchType, setSearchType] = useState('participantEmail');
  const { profile } = useProfileState();

  const { data: analyticsData, isLoading: isAnalyticsLoading } = useQuery(['user-forms-analytics'], () => fetcher(fetchUserFormAnalytics({})));
  const debouncedSearch = useDebounce(search, 500);
  const { data, isLoading } = useQuery(['user-forms',  { search: debouncedSearch, page: currentPage }], () => fetcher(fetchUserFormList({ page: currentPage, limit: 25, search: debouncedSearch, searchType })), {
    keepPreviousData: true
  });

  function hasContractAccess(contract = {}) {
    return contract?.user === profile._id || contract.watchers?.includes(contract?.user)
  }

  const columns = useMemo(() => 
 [
    {
        title: 'Nombre',
        accessor: 'particpant.name',
        render: (row) => row?.participant?.obligation === 'legal' ? row?.participant.name : `${row?.participant.name || ''} ${row?.participant.last_name || ''}`
    },
    {
        title: 'Correo',
        accessor: 'participant.email'
    },
    {
      title: 'Estatus',
      accessor: 'status',
      // eslint-disable-next-line no-nested-ternary
      render: (row) => row?.status === 'completed' ? 'Completado' : row?.status === 'signed' ? 'Concluido' : 'Pendiente'
    },
    {
      title: 'Id del documento',
      accessor: 'contract.documentId',
      render: (row) => hasContractAccess(row?.contract) ? <Link to={`/contracts/editor/${row?.contract?._id}`}>{row?.contract?.documentId}</Link> : `${row?.contract?.documentId || ''}`
    },
    {
      title: 'Formulario',
      accessor: 'template.name',
      render: (row) => `${row?.templateName || row?.template?.name || ''}`
    },
    {
      title: 'Días',
      accessor: 'createdAt',
      render: (row) => {
        let startDate = row.createdAt;
        // eslint-disable-next-line no-nested-ternary
        let endDate = row.status === 'completed' ? row.updatedAt : row.status === 'signed' ? row.signedAt : new Date();
        const diffInDays = getDateDiffInDays(startDate, endDate)
        
        return  <Badge
            className="d-inline px-3"
            color={`${getBadgeColor(diffInDays)}`}
        >
            {diffInDays}
        </Badge>
      }
    },
    {
      title: 'Fecha de creación',
      accessor: 'createdAt',
      render: (row) => parseDate(row.createdAt)
    },
    {
      title: 'Última actualización',
      accessor: 'updatedAt',
      render: (row) => parseDate(row.updatedAt)
    },
], []);

const onSearchTypeChange = (evt) => { setSearchType(evt.target.value) }
const onSearchChange = (evt) => { setSearch(evt.target.value) }
const handlePageChange = useCallback(
  () => {
  },
  [],
)
const handleRowClick = useCallback(
  (row) => {
    setCurrentUserForm(row)
    setIsOpen(true)
  },
  [],
)
const handlePageClick = useCallback(
  (page) => {
    setCurrentPage(page)
  },
  [],
)
const handleNextPage = useCallback(
  (page) => {
    setCurrentPage((currentPage) => Math.min(currentPage + 1, data?.data?.pages))
  },
  [data?.data?.pages],
)
const handlePreviousPage = useCallback(
  () => {
    setCurrentPage((currentPage) => Math.max(currentPage - 1, 1))
  },
  [],
)
const toggle = () => setIsOpen(isOpen => !isOpen)

console.log({data, currentUserForm, analyticsData})
  return (
    <Row className="">
        <Col md="12" className="mt-3">
            <MainHeader title={t("Mis Formularios")} showLogo={false}>
              <InputGroup inlist="true" className="align-items-center">
                  <Input type="select" className="p-0 mr-1" onChange={onSearchTypeChange} value={searchType}>
                      <option value="participantEmail">{t('Correo participante')}</option>
                      <option value="participantName">Nombre participante</option>
                  </Input>
                  <Input type="text" placeholder={t('Buscar')} onChange={onSearchChange} />
              </InputGroup>
            </MainHeader>
            <Row className="mb-3">
              <Col>
                <CircleGraph 
                  stats={analyticsData}
                  title={''}
                  isFetching={isAnalyticsLoading}
                  handleButtonClick={noop}
                  t={t}
                />
              </Col>
              <Col>
                <AvgStats 
                  stats={analyticsData}
                  isFetching={isAnalyticsLoading}
                  t={t}
                />
              </Col>
            </Row>
            <MainTable 
              data={data?.data?.docs || []}
              currentPage={currentPage}
              totalPages={data?.data?.pages}
              onNextPage={handleNextPage}
              onPreviousPage={handlePreviousPage}
              onPageClick={handlePageClick}
              columns={columns}
              onClick={handleRowClick}
              isLoading={isLoading}
            />
            <UserFormDetail 
              isOpen={isOpen}
              title={`${currentUserForm?.template?.name || ''} - ${currentUserForm?.participant?.email || ''}`}
              onClose={toggle}
              defaultTab="participant"
              currentUserForm={currentUserForm}
            />
        </Col>
    </Row>
  )
}

function CircleGraph({ handleButtonClick, currentList, title, stats = {}, t, i, isFetching }) {
  const data = useMemo(() => [
    { angle: stats.signedUserForms || 0, radius: 1.4, innerRadius: 2.2, color: 1 },
    { angle: stats.completedUserForms || 0, radius: 1.4, innerRadius: 2.2, color: 2 },
    { angle: stats.incompleteUserForms || 0, radius: 1.4, innerRadius: 2.2, color: 3 }
  ], [stats.incompleteUserForms, stats.completedUserForms, stats.signedUserForms])

  return (
      <Card style={{ minWidth: '300px', minHeight: '135px' }} className={clsx('card-shadow rounded-lg mt-2', isFetching && 'pulse-animation')}>
          <CardBody className="p-3 mx-1">
              <p className="font-weight-bold m-0 mb-3">{title}</p>
              <Media  className="d-flex align-items-center">
                  <Media className="mr-2">
                    <RadialChart height={70} width={70} data={data} colorRange={['#F47B33', '#344e5b','#6c757d']} />
                  </Media>
                  <Media body className="ml-2 align-self-center">
                      <DataLabel
                        label="Concluidos"
                        percentage={stats.signedUserForms / stats.totalUserForms * 100}
                        value={stats.signedUserForms}
                        color={'#F47B33'}
                      />
                      <DataLabel
                        className="mt-3"
                        label="Completados"
                        percentage={stats.completedUserForms / stats.totalUserForms * 100}
                        value={stats.completedUserForms}
                        color={'#344e5b'}
                      />
                      <DataLabel
                        className="mt-3"
                        label="Pendientes"
                        percentage={stats.incompleteUserForms / stats.totalUserForms * 100}
                        value={stats.incompleteUserForms}
                        color={'#6c757d'}
                      />
                  </Media>
              </Media>
          </CardBody>
      </Card>

  )
}

function AvgStats({ stats = {}, isFetching }) {
  return (
      <Card style={{ minWidth: '300px', minHeight: '135px' }} className={clsx('card-shadow rounded-lg mt-2', isFetching && 'pulse-animation')}>
          <CardBody className="p-3 mx-1 d-flex align-items-center w-100">
            <ListGroup flush className="rounded-lg w-100">
           
            <ListGroupItem>
                <div className="d-flex">
                    <div className="align-self-center">
                        <span className={clsx('font-weight-bold h5')}>
                            {stats.avgDaysToCompletedUserForms?.toFixed(2)}
                        </span>
                        <span className="text-muted ml-2">
                            Promedio de dias para completar formulario
                        </span>
                    </div>
                </div>
            </ListGroupItem>
            <ListGroupItem>
                <div className="d-flex">
                    <div className="align-self-center">
                        <span className={clsx('font-weight-bold h5')}>
                            {stats.avgDaysToSignedUserForms?.toFixed(2)}
                        </span>
                        <span className="text-muted ml-2">
                          Promedio de días para firmar formulario 
                        </span>
                    </div>
                </div>
            </ListGroupItem>    
          </ListGroup>
                      
          </CardBody>
      </Card>

  )
}

function DataLabel({ className, id, label, value, isActive, percentage, color, onButtonClick = noop }) {
  const { t } = useTranslation();
  return (
    <div className={clsx("d-flex align-items-center", className)}>
        <div>
            <i className="fas fa-circle mr-2" style={{color}}></i>
            <span className="font-weight-bold">{`${value}`}</span> {label}
            <span className="text-muted">{' '}({percentage.toFixed(2)}%)</span>
        </div>
        {/* <div className="ml-auto">
            <Badge
                href="#"
                className={clsx(isActive && 'text-white')}
                color={clsx(isActive ? 'primary' : 'light')}
                onClick={() => onButtonClick(id)}
            >
                {t('minidashboard.show')}
            </Badge>
        </div> */}
    </div>
)
}


function TableRow({ item, columns = [], actions = [], onDoubleClick = noop, onClick = noop, onCheck = noop }) {
  const handleCheck = useCallback((e) => {
      e.stopPropagation();
      // e.preventDefault();
      onCheck(item)
  }, [item, onCheck]);

  const handleDoubleClick = useCallback((e) => {
    onDoubleClick(item);
  }, [onDoubleClick, item])


  const handleClick = useCallback((e) => {
    onClick(item)
  }, [onClick, item])

  return (
      <tr onDoubleClick={handleDoubleClick} onClick={handleClick} className="pointer">
          <TableCell className="align-items-center text-center align-middle">
              <CustomInput id={item._id} type="checkbox" name="list-item" onChange={handleCheck} />
          </TableCell>
          {
            columns.map(col => (
              <TableCell key={col.accessor} className={col.className} colSpan={col.colSpan}>
                {col.render ? col.render(item) : `${getKey(item, col.accessor) || ''}`}
              </TableCell>
            ))
          }
          {
            !!actions.length &&
            <TableCell className="align-items-center text-center align-middle text-nowrap">
              <UncontrolledDropdown onClick={(e) => e.stopPropagation()}>
                  <DropdownToggle color="light">
                      <i className="fas fa-bars"></i>
                  </DropdownToggle>
                  <MenuDropdown item={item} />
              </UncontrolledDropdown>
            </TableCell>
          }
          
      </tr>
  )
}

const TableCell = ({ className, children, colSpan }) => {
  return <td className={className} colSpan={colSpan}>{children}</td>
}

function TableBody({ isLoading, data = [],  columns, onClick, onDoubleClick }) {

  const { t } = useTranslation();

  const colSpan = columns.reduce((total, col) => {
    total += col.colSpan || 1;
    return total;
  }, 1)

  if (isLoading) {
      return <tr><td colSpan={colSpan}><Spinner size="sm"></Spinner>{' '}{t('templates.listing.loading')}</td></tr>
  }
  if (!data.length) {
      return <tr><td colSpan={colSpan}>{t('templates.favourites.empty')}</td></tr>
  }
  return (
    data.map(item => <TableRow item={item} columns={columns} key={item._id} onClick={onClick} onDoubleClick={onDoubleClick} />)
  )
}

function MainTable({ data, columns = [], actions = [], currentPage = 1, totalPages = 1, isAllSelected, isLoading, onSelectAll = noop, onPreviousPage = noop, onNextPage = noop, onPageClick = noop, onClick = noop, onDoubleClick = noop }) {

  return (<>
     <Card className="border bg-white card-shadow rounded-lg" style={{ minHeight: '250px', height: 'calc(100% - 60px)'}}>
          <CardBody className="mt-2 h-100 overflow-y-hidden">
              <Table size="sm" className="bg-white text-center h-100 table-hover mb-0" style={{ maxHeight: '50px' }}>
                  <thead>
                      <tr className="text-center">
                          <th className="text-center">
                            <CustomInput type="checkbox" onChange={onSelectAll} checked={isAllSelected} id="check-all" />
                          </th>
                          {columns.map(col => (
                            <th className="text-center" colSpan={col.colSpan}>{col.title}</th>
                          ))}
                          { !!actions.length && <th className="text-center"></th> }
                      </tr>
                  </thead>
                  <tbody className="text-center">
                      <TableBody data={data} columns={columns} actions={actions} isLoading={isLoading} onClick={onClick} onDoubleClick={onDoubleClick} />
                  </tbody>
              </Table>
          </CardBody>
          <CardFooter className="text-center" style={{ borderBottomLeftRadius: '22px', borderBottomRightRadius: '22px' }}>
              <Pagination
                  //{...templates.meta}
                  page={currentPage}
                  total={totalPages}
                  isDisabled={isLoading}
                  onNextPage={onNextPage}
                  onPrevPage={onPreviousPage}
                  onPageClick={onPageClick}
              />
          </CardFooter>
      </Card>
  </>
  )
}

function MenuDropdown({ template }) {
  const { t } = useTranslation();
  const availableActions = [];

  return (
      <DropdownMenu right>
          {
              availableActions.map((action, i) => {
                  const { faIcon, label, onClick } = action;
                  return (
                      <DropdownItem key={`${action.name}-${i}`} href="#" onClick={onClick} className="contract-dropdown-item">
                          <i className={clsx(!faIcon.includes('fab') ? `fas fa-${faIcon}` : faIcon, 'mr-2')}></i>{label}  <br />
                      </DropdownItem>
                  )
              })
          }
      </DropdownMenu>
  )
}
